import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../firebase/AuthContext';
import UserHeader from './UserHeader';
import './TopicTagsAggregate.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const MIN_FONT_PX = 14;
const MAX_FONT_PX = 44;

const computeTagFontSize = (count, counts) => {
  if (!counts.length) return (MIN_FONT_PX + MAX_FONT_PX) / 2;
  const min = Math.min(...counts);
  const max = Math.max(...counts);
  if (max === min) return (MIN_FONT_PX + MAX_FONT_PX) / 2;
  const logMin = Math.log(min);
  const logMax = Math.log(max);
  const t = (Math.log(count) - logMin) / (logMax - logMin);
  return MIN_FONT_PX + t * (MAX_FONT_PX - MIN_FONT_PX);
};

const TopicTagsAggregate = () => {
  const { sessionId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tags, setTags] = useState([]);
  const [stats, setStats] = useState(null);

  const authHeaders = useCallback(
    () => ({
      Authorization: `Bearer ${sessionId}`,
      'Content-Type': 'application/json',
    }),
    [sessionId]
  );

  const fetchAggregate = useCallback(async () => {
    try {
      if (!sessionId) {
        alert('ログインが必要です');
        return;
      }
      const response = await fetch(
        `${API_BASE_URL}/admin/topic-tags-aggregate?email_prefix=reibow`,
        { method: 'GET', headers: authHeaders() }
      );
      if (!response.ok) throw new Error('話題タグ集計の取得に失敗しました');
      const data = await response.json();
      setTags(data.tags || []);
      setStats({
        totalDocsWithTags: data.total_docs_with_tags ?? 0,
        uniqueTagCount: data.unique_tag_count ?? 0,
        totalTagOccurrences: data.total_tag_occurrences ?? 0,
      });
    } catch (error) {
      console.error('Error fetching topic tags aggregate:', error);
      alert(`話題タグ集計の取得に失敗しました: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [sessionId, authHeaders]);

  useEffect(() => {
    setLoading(true);
    fetchAggregate();
  }, [fetchAggregate]);

  const counts = useMemo(() => tags.map((item) => item.count), [tags]);

  return (
    <div className="topic-tags-aggregate">
      <UserHeader />
      <div className="topic-tags-aggregate-container">
        <div className="topic-tags-aggregate-header">
          <Link to="/mantra-admin" className="topic-tags-aggregate-back-btn">
            評価管理へ戻る
          </Link>
          <h1>話題タグ集計</h1>
          <span aria-hidden="true" style={{ width: 120 }} />
        </div>

        {loading ? (
          <div className="topic-tags-aggregate-loading">読み込み中...</div>
        ) : (
          <>
            {stats ? (
              <div className="topic-tags-aggregate-stats">
                <span>
                  タグ付きコメント: <strong>{stats.totalDocsWithTags}</strong> 件
                </span>
                <span>
                  ユニークタグ: <strong>{stats.uniqueTagCount}</strong> 件
                </span>
                <span>
                  タグ出現回数合計: <strong>{stats.totalTagOccurrences}</strong> 回
                </span>
              </div>
            ) : null}

            {tags.length === 0 ? (
              <div className="topic-tags-aggregate-empty">表示する話題タグがありません</div>
            ) : (
              <div className="topic-tags-aggregate-cloud" role="list" aria-label="話題タグクラウド">
                {tags.map(({ tag, count }) => (
                  <span
                    key={tag}
                    role="listitem"
                    className="topic-tags-aggregate-tag"
                    style={{ fontSize: `${computeTagFontSize(count, counts)}px` }}
                    title={`${tag}: ${count}件のコメント`}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TopicTagsAggregate;
